import React, { useEffect, useState, Component } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, UploadIcon, AlertCircleIcon, EyeIcon, EyeOffIcon, UserIcon, MailIcon, LockIcon } from 'lucide-react';
import { useAuth } from './auth/AuthProvider';
import { useNavigate, useLocation } from 'react-router-dom';
// Password strength checker
const checkPasswordStrength = (password: string): {
  strength: 'weak' | 'medium' | 'strong';
  color: string;
  width: string;
  message: string;
} => {
  if (!password) {
    return {
      strength: 'weak',
      color: 'bg-gray-200 dark:bg-gray-700',
      width: '0%',
      message: ''
    };
  }
  // Check for minimum length
  const hasMinLength = password.length >= 8;
  // Check for character variety
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumbers = /[0-9]/.test(password);
  const hasSpecialChars = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
  const criteriaCount = [hasMinLength, hasUppercase, hasLowercase, hasNumbers, hasSpecialChars].filter(Boolean).length;
  if (criteriaCount <= 2) {
    return {
      strength: 'weak',
      color: 'bg-red-500',
      width: '33%',
      message: 'Weak password'
    };
  } else if (criteriaCount <= 4) {
    return {
      strength: 'medium',
      color: 'bg-yellow-500',
      width: '66%',
      message: 'Medium strength'
    };
  } else {
    return {
      strength: 'strong',
      color: 'bg-green-500',
      width: '100%',
      message: 'Strong password'
    };
  }
};
export const SubmissionForm = () => {
  const {
    isAuthenticated,
    user,
    login
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    sellerName: '',
    email: '',
    password: '',
    productName: '',
    description: '',
    category: '',
    uploadLink: '',
    price: '',
    demoLink: '',
    acceptTerms: false
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error' | 'email_exists'>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    strength: 'weak',
    color: 'bg-gray-200 dark:bg-gray-700',
    width: '0%',
    message: ''
  });
  // Check if we need to redirect to auth
  useEffect(() => {
    if (!isAuthenticated) {
      // Redirect to auth if not authenticated
      navigate('/sell-with-us', {
        state: {
          openAuth: true
        }
      });
    }
  }, [isAuthenticated, navigate]);
  // Scroll to form if coming from redirect
  useEffect(() => {
    if (location.state && location.state.scrollToForm && isAuthenticated) {
      const submissionForm = document.getElementById('submission-form');
      if (submissionForm) {
        submissionForm.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }
  }, [location, isAuthenticated]);
  // Prefill form data if user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      setFormData(prev => ({
        ...prev,
        sellerName: user.name || '',
        email: user.email || ''
      }));
    }
  }, [isAuthenticated, user]);
  // Update password strength when password changes
  useEffect(() => {
    setPasswordStrength(checkPasswordStrength(formData.password));
  }, [formData.password]);
  const categories = ['Website Templates', 'Mobile App Kits', 'UI Kits', 'AI Prompt Packs', 'Component Libraries', 'Design Systems', 'Animations', 'Audio Packs', 'No-Code MVPs', 'Notion Templates', 'eBooks & PDF Guides', 'Course Slides & Templates', 'Printable Planners', 'Email Templates & Flows', 'Copywriting Packs', 'Fonts & Typefaces', 'Icon Packs', '3D Assets & Mockups', 'Canva Templates', 'Social Media Content Packs', 'Legal Templates', 'Resume & Portfolio Templates', 'Other'];
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
    // Clear error when field is being edited
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = {
          ...prev
        };
        delete newErrors[name];
        return newErrors;
      });
    }
    // Reset form status if it was email_exists and user changes email
    if (formStatus === 'email_exists' && name === 'email') {
      setFormStatus('idle');
    }
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    // Only validate seller info if not authenticated
    if (!isAuthenticated) {
      if (!formData.sellerName.trim()) newErrors.sellerName = 'Seller name is required';
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
        newErrors.email = 'Please enter a valid email';
      }
      if (!formData.password) {
        newErrors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      }
    }
    // Always validate product info
    if (!formData.productName.trim()) newErrors.productName = 'Product name is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.category) newErrors.category = 'Please select a category';
    if (!formData.uploadLink.trim()) newErrors.uploadLink = 'Upload link is required';
    if (!formData.price.trim()) {
      newErrors.price = 'Price is required';
    } else if (isNaN(Number(formData.price)) || Number(formData.price) <= 0) {
      newErrors.price = 'Please enter a valid price';
    }
    if (!formData.acceptTerms) {
      newErrors.acceptTerms = 'You must confirm your product follows our rules';
    }
    return newErrors;
  };
  const checkEmailExists = async (email: string): Promise<boolean> => {
    // This would be an API call in a real application
    // For demo purposes, we'll simulate some existing emails
    const existingEmails = ['test@example.com', 'seller@marketplace.com', 'demo@example.com'];
    return new Promise(resolve => {
      setTimeout(() => {
        resolve(existingEmails.includes(email.toLowerCase()));
      }, 500);
    });
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setFormStatus('submitting');
    // Check if email exists (only if not authenticated)
    if (!isAuthenticated) {
      const emailExists = await checkEmailExists(formData.email);
      if (emailExists) {
        setFormStatus('email_exists');
        setErrors(prev => ({
          ...prev,
          email: 'This email is already registered'
        }));
        return;
      }
    }
    // Simulate form submission and account creation
    setTimeout(() => {
      // If not authenticated, create account and log in
      if (!isAuthenticated) {
        // In a real app, this would be an API call to create a user account
        // Simulate login after account creation
        login({
          name: formData.sellerName,
          email: formData.email,
          // Always include both buyer and seller roles
          roles: ['buyer', 'seller'],
          activeRole: 'seller',
          isSeller: true
        });
      }
      // Submit product information
      setFormStatus('success');
    }, 1500);
  };
  const handleSignIn = () => {
    // In a real app, this would open the sign-in modal or redirect to sign-in page
    // For this demo, we'll just reset the form status
    setFormStatus('idle');
  };
  if (formStatus === 'success') {
    return <motion.div className="bg-white dark:bg-[#162825] p-8 rounded-2xl shadow-sm border border-gray-100 dark:border-[#1A3330] text-center" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.5
    }} data-id="element-2835">
        <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-2836">
          <CheckIcon className="h-8 w-8 text-green-600 dark:text-green-400" data-id="element-2837" />
        </div>
        <h3 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-4" data-id="element-2838">
          Submission Received!
        </h3>
        <p className="text-gray-600 dark:text-[#B0C1BD] mb-6" data-id="element-2839">
          Thank you for submitting your product. Our team will review it within
          24-72 hours and get back to you via email.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2840">
          <button onClick={() => navigate('/seller-dashboard')} className="bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white px-6 py-3 rounded-full text-base font-medium hover:shadow-lg transition-all" data-id="element-2841">
            Go to Dashboard
          </button>
          <button onClick={() => {
          setFormData({
            sellerName: isAuthenticated && user ? user.name : '',
            email: isAuthenticated && user ? user.email : '',
            password: '',
            productName: '',
            description: '',
            category: '',
            uploadLink: '',
            price: '',
            demoLink: '',
            acceptTerms: false
          });
          setFormStatus('idle');
        }} className="bg-gray-100 dark:bg-[#1A3330] text-gray-800 dark:text-[#B0C1BD] px-6 py-3 rounded-full text-base font-medium hover:bg-gray-200 dark:hover:bg-[#0B2C2A] transition-all" data-id="element-2842">
            Submit Another Product
          </button>
        </div>
      </motion.div>;
  }
  if (formStatus === 'email_exists') {
    return <motion.div className="bg-white dark:bg-[#162825] p-8 rounded-2xl shadow-sm border border-gray-100 dark:border-[#1A3330] text-center" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.5
    }} data-id="element-2843">
        <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-2844">
          <MailIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" data-id="element-2845" />
        </div>
        <h3 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-4" data-id="element-2846">
          Account Already Exists
        </h3>
        <p className="text-gray-600 dark:text-[#B0C1BD] mb-6" data-id="element-2847">
          The email <span className="font-medium" data-id="element-2848">{formData.email}</span> is
          already registered. Please sign in to continue.
        </p>
        <div className="flex justify-center space-x-4" data-id="element-2849">
          <button onClick={handleSignIn} className="bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white px-6 py-3 rounded-full text-base font-medium hover:shadow-lg transition-all" data-id="element-2850">
            Sign In
          </button>
          <button onClick={() => {
          setFormData(prev => ({
            ...prev,
            email: ''
          }));
          setFormStatus('idle');
        }} className="bg-gray-100 dark:bg-[#1A3330] text-gray-800 dark:text-[#B0C1BD] px-6 py-3 rounded-full text-base font-medium hover:bg-gray-200 dark:hover:bg-[#0B2C2A] transition-all" data-id="element-2851">
            Use Different Email
          </button>
        </div>
      </motion.div>;
  }
  // If not authenticated, don't show the form
  if (!isAuthenticated) {
    return <div className="bg-white dark:bg-[#162825] p-8 rounded-2xl shadow-sm border border-gray-100 dark:border-[#1A3330] text-center" data-id="element-2852">
        <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-2853">
          <UserIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" data-id="element-2854" />
        </div>
        <h3 className="text-2xl font-bold text-gray-800 dark:text-[#F8FAF9] mb-4" data-id="element-2855">
          Authentication Required
        </h3>
        <p className="text-gray-600 dark:text-[#B0C1BD] mb-6" data-id="element-2856">
          Please sign in or create an account to submit your product.
        </p>
      </div>;
  }
  return <motion.form onSubmit={handleSubmit} className="bg-white dark:bg-[#162825] p-8 rounded-2xl shadow-sm border border-gray-100 dark:border-[#1A3330]" initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.5
  }} data-id="element-2857">
      {isAuthenticated && user && <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/30 rounded-lg p-4 mb-6" data-id="element-2858">
          <p className="text-blue-800 dark:text-blue-300 flex items-center" data-id="element-2859">
            <UserIcon className="h-5 w-5 mr-2" data-id="element-2860" />
            You're signed in as{' '}
            <span className="font-medium ml-1" data-id="element-2861">{user.email}</span>. Add a new
            product below.
          </p>
        </div>}
      <div className="space-y-6" data-id="element-2862">
        {/* Product Information */}
        <div className="mb-8" data-id="element-2863">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-[#F8FAF9] mb-4 pb-2 border-b border-gray-100 dark:border-[#1A3330]" data-id="element-2864">
            Product Information
          </h3>
          <div className="space-y-6" data-id="element-2865">
            <div data-id="element-2866">
              <label htmlFor="productName" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-2867">
                Product Name *
              </label>
              <input type="text" id="productName" name="productName" value={formData.productName} onChange={handleChange} className={`w-full px-4 py-2 rounded-lg border ${errors.productName ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-[#1A3330]'} focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent dark:bg-[#0F1F1D] dark:text-[#B0C1BD]`} placeholder="E.g. Premium Website Template" data-id="element-2868" />
              {errors.productName && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-2869">
                  {errors.productName}
                </p>}
            </div>
            <div data-id="element-2870">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-2871">
                Short Description *
              </label>
              <textarea id="description" name="description" value={formData.description} onChange={handleChange} rows={3} className={`w-full px-4 py-2 rounded-lg border ${errors.description ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-[#1A3330]'} focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent dark:bg-[#0F1F1D] dark:text-[#B0C1BD]`} placeholder="Briefly describe your product (max 200 words)" data-id="element-2872" />
              {errors.description && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-2873">
                  {errors.description}
                </p>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2874">
              <div data-id="element-2875">
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-2876">
                  Product Category *
                </label>
                <select id="category" name="category" value={formData.category} onChange={handleChange} className={`w-full px-4 py-2 rounded-lg border ${errors.category ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-[#1A3330]'} focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent dark:bg-[#0F1F1D] dark:text-[#B0C1BD]`} data-id="element-2877">
                  <option value="" disabled data-id="element-2878">
                    Select a category
                  </option>
                  {categories.map(category => <option key={category} value={category} data-id="element-2879">
                      {category}
                    </option>)}
                </select>
                {errors.category && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-2880">
                    {errors.category}
                  </p>}
              </div>
              <div data-id="element-2881">
                <label htmlFor="price" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-2882">
                  Price (USD) *
                </label>
                <input type="text" id="price" name="price" value={formData.price} onChange={handleChange} className={`w-full px-4 py-2 rounded-lg border ${errors.price ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-[#1A3330]'} focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent dark:bg-[#0F1F1D] dark:text-[#B0C1BD]`} placeholder="E.g. 49.99" data-id="element-2883" />
                {errors.price && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-2884">
                    {errors.price}
                  </p>}
              </div>
            </div>
          </div>
        </div>
        {/* Files and Links */}
        <div className="mb-8" data-id="element-2885">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-[#F8FAF9] mb-4 pb-2 border-b border-gray-100 dark:border-[#1A3330]" data-id="element-2886">
            Files and Links
          </h3>
          <div className="space-y-6" data-id="element-2887">
            <div data-id="element-2888">
              <label htmlFor="uploadLink" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-2889">
                Upload Link *
              </label>
              <div className="flex items-center" data-id="element-2890">
                <input type="text" id="uploadLink" name="uploadLink" value={formData.uploadLink} onChange={handleChange} className={`w-full px-4 py-2 rounded-lg border ${errors.uploadLink ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-[#1A3330]'} focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent dark:bg-[#0F1F1D] dark:text-[#B0C1BD]`} placeholder="Google Drive, Dropbox, or any cloud storage link" data-id="element-2891" />
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-2892">
                Provide a link to your product files (Google Drive, Dropbox,
                WeTransfer, etc.)
              </p>
              {errors.uploadLink && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-2893">
                  {errors.uploadLink}
                </p>}
            </div>
            <div data-id="element-2894">
              <label htmlFor="demoLink" className="block text-sm font-medium text-gray-700 dark:text-[#C4D1CC] mb-1" data-id="element-2895">
                Demo/Preview Link (Optional)
              </label>
              <input type="text" id="demoLink" name="demoLink" value={formData.demoLink} onChange={handleChange} className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-[#1A3330] focus:outline-none focus:ring-2 focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] focus:border-transparent dark:bg-[#0F1F1D] dark:text-[#B0C1BD]" placeholder="Link to a live demo or preview" data-id="element-2896" />
              <p className="mt-1 text-xs text-gray-500 dark:text-[#7A8F8A]" data-id="element-2897">
                If available, provide a link where buyers can preview your
                product
              </p>
            </div>
          </div>
        </div>
        {/* Terms and Submission */}
        <div className="pt-4" data-id="element-2898">
          <div className="flex items-start mb-6" data-id="element-2899">
            <div className="flex items-center h-5" data-id="element-2900">
              <input id="acceptTerms" name="acceptTerms" type="checkbox" checked={formData.acceptTerms} onChange={handleChange} className="focus:ring-[#2DB489] dark:focus:ring-[#4ED1A0] h-4 w-4 text-[#2DB489] dark:text-[#4ED1A0] border-gray-300 dark:border-[#1A3330] rounded" data-id="element-2901" />
            </div>
            <div className="ml-3" data-id="element-2902">
              <label htmlFor="acceptTerms" className={`text-sm ${errors.acceptTerms ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-[#C4D1CC]'}`} data-id="element-2903">
                I confirm this product follows all rules and is original *
              </label>
              {errors.acceptTerms && <p className="mt-1 text-sm text-red-600 dark:text-red-400" data-id="element-2904">
                  {errors.acceptTerms}
                </p>}
            </div>
          </div>
          <div className="bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-100 dark:border-yellow-900/30 rounded-lg p-4 mb-6" data-id="element-2905">
            <div className="flex" data-id="element-2906">
              <AlertCircleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mr-2 flex-shrink-0" data-id="element-2907" />
              <p className="text-sm text-yellow-700 dark:text-yellow-300" data-id="element-2908">
                All submissions are manually reviewed for quality and safety
                before going live. This process typically takes 24-72 hours.
              </p>
            </div>
          </div>
          <button type="submit" disabled={formStatus === 'submitting'} className={`w-full bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white px-6 py-3 rounded-full text-base font-medium hover:shadow-lg transition-all ${formStatus === 'submitting' ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-2909">
            {formStatus === 'submitting' ? <span className="flex items-center justify-center" data-id="element-2910">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-2911">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-2912"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-2913"></path>
                </svg>
                Submitting...
              </span> : 'Submit Product for Review'}
          </button>
        </div>
      </div>
    </motion.form>;
};